'use client';

import { memo, useState } from 'react';
import {
  CloseButton,
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import clsx from 'clsx';

const VirturalTour: React.FC<{
  src?: string;
}> = ({ src }) => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  if (!src) {
    return null;
  }
  const urlSrc = new URL(src);
  urlSrc.searchParams.set('nt', '1');
  urlSrc.searchParams.set('play', '1');
  const frameSrc = urlSrc.toString();
  const frame = (
    <iframe
      height="100%"
      width="100%"
      src={frameSrc}
      seamless
      allow="autoplay; web-share; xr-spatial-tracking; vr;"
      id="matterport-iframe"
    ></iframe>
  );

  return (
    <>
      <button
        onClick={handleOpen}
        type="button"
        aria-label="View Virtual Tour"
        className="ut-md group flex flex-nowrap items-center gap-1.5 whitespace-nowrap !p-0 !font-semibold text-secondary-main hover:underline"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
        >
          <path
            fill="currentColor"
            d="M5.5 16.8c-.7 0-1.2-.6-1.2-1.2c0-.7.6-1.2 1.2-1.2s1.2.6 1.2 1.2c.1.6-.5 1.2-1.2 1.2m0-4.8C3.6 12 2 13.6 2 15.5C2 18.1 5.5 22 5.5 22S9 18.1 9 15.5C9 13.6 7.4 12 5.5 12M4 6c-.6 0-1 .4-1 1v3.6c.6-.3 1.3-.5 2-.6V8h10v8h-4c-.1.7-.2 1.4-.5 2H16c.6 0 1-.4 1-1v-3.5l4 4v-11l-4 4V7c0-.6-.4-1-1-1z"
          />
        </svg>
        <span className="text-inherit">Virtual Tour</span>
      </button>
      <Dialog open={open} onClose={handleClose}>
        <DialogBackdrop
          transition
          className="fixed inset-0 z-50 h-full w-dvw bg-[#00000080] bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />
        <DialogTitle className="sr-only">Virtual Tour</DialogTitle>
        <div className="fixed left-0 top-0 z-50 h-full w-full">
          <div className="relative">
            <DialogPanel
              transition
              className={clsx(
                'relative h-dvh bg-[#111] transition-all',
                'data-[closed]:!translate-y-[100dvh] data-[open]:translate-y-0 data-[closed]:opacity-0 data-[open]:opacity-100',
                'data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in',
                'data-[closed]:lg:!translate-y-4 data-[closed]:lg:scale-95 data-[closed]:lg:opacity-0',
                'data-[enter]:lg:duration-300 data-[leave]:lg:duration-200 data-[enter]:lg:ease-out data-[leave]:lg:ease-in'
              )}
            >
              <div className="flex h-fit w-full items-center justify-end bg-[#111] px-4 py-2">
                <CloseButton
                  className="top-tool-button group h-12 w-12 animate-fade-in rounded-full bg-[#222] p-3 text-white hover:bg-[#111] hover:text-[#ff3168]"
                  onClick={handleClose}
                  title="Close"
                  aria-label="Close virtual tour"
                >
                  <svg
                    className="group-hover:scale-110"
                    xmlns="http://www.w3.org/2000/svg"
                    width="13"
                    height="13"
                    viewBox="0 0 13 13"
                    fill="none"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M0.210037 0.429754C-0.0693482 0.709139 -0.0693486 1.16211 0.210036 1.4415L5.26852 6.49998L0.209539 11.559C-0.069847 11.8384 -0.0698463 12.2913 0.209539 12.5707C0.488924 12.8501 0.941896 12.8501 1.22128 12.5707L6.28027 7.51173L11.3392 12.5707C11.6186 12.8501 12.0716 12.8501 12.351 12.5707C12.6303 12.2913 12.6303 11.8383 12.351 11.5589L7.29201 6.49998L12.3505 1.44154C12.6298 1.16215 12.6298 0.70918 12.3505 0.429795C12.0711 0.15041 11.6181 0.150409 11.3387 0.429794L6.28027 5.48824L1.22178 0.429754C0.942395 0.150369 0.489422 0.150369 0.210037 0.429754Z"
                      fill="currentColor"
                    />
                  </svg>
                </CloseButton>
              </div>
              <div className="h-[91dvh] sm:h-[94dvh]">{frame}</div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default memo(VirturalTour);
